const toUnixTimeStamp = (date) => Math.round(date.valueOf() / 1000);
const toValidUnixTimeStamp = (date) => {
    return date ? Math.round(date.valueOf() / 1000) : undefined;
};
const addDaysOnDate = (currentDate = new Date(), daysToAdd = 1, hoursToAdd = 0, minsToAdd = 0, secsToAdd = 0) => {
    const nextDay = new Date(currentDate);
    nextDay.setDate(nextDay.getDate() + daysToAdd);
    if (hoursToAdd) {
        nextDay.setHours(nextDay.getHours() + hoursToAdd);
    }
    if (minsToAdd) {
        nextDay.setMinutes(nextDay.getMinutes() + minsToAdd);
    }
    if (secsToAdd) {
        nextDay.setSeconds(nextDay.getSeconds() + secsToAdd);
    }
    return nextDay;
};
const isSameDate = (date1, date2) => {
    return date1.getTime() === date2.getTime();
};
const getStartingTimeOfDate = (date) => {
    const dt = new Date(date);
    dt.setHours(0, 0, 0, 0);
    return dt;
};
const getEndingTimeOfDate = (date) => {
    const dt = new Date(date);
    dt.setHours(23, 59, 59, 999);
    return dt;
};
/**
 * Formats a date string according to the provided format.
 * @param {string} inputDate - The input date string to be formatted.
 * @param {string} format - The custom format specifying how the date should be formatted.
 * @returns {string} The formatted date string.
 */
const formatDate = (inputDate, format, fallback) => {
    /**
     * (2023-09-16,DD/MM/YYYY)=> 16/09/2023
     * (2022-03-05,MMMM DD, YYYY)=> March 05, 2022
     * (2021-12-25,"YYYY [is the year] MM [is the month] DD [is the day]")=> '2021 is the year 12 is the month 25 is the day'
     * ("2021-12-25T18:45:30.500Z","YYYY-MM-DD hh:mm:ss.SSS")=> 2021-12-25 18:45:30.500
     */
    // Parse the input date using JavaScript's Date object
    let date = undefined;
    if (typeof inputDate === "string" || typeof inputDate === "number") {
        date = new Date(inputDate);
    }
    if (inputDate instanceof Date) {
        date = inputDate;
    }
    // Check if the input date is valid
    if (!(date instanceof Date) || isNaN(date.getTime())) {
        return fallback !== null && fallback !== void 0 ? fallback : "Invalid Date";
    }
    const hours24 = date.getHours();
    const hours12 = hours24 % 12 || 12;
    const amOrPm = hours24 >= 12 ? "PM" : "AM";
    // Create a dictionary to replace format placeholders
    const formatOptions = {
        YYYY: date.getFullYear(),
        YY: date.getFullYear().toString().slice(-2),
        MMMM: new Intl.DateTimeFormat("en-US", { month: "long" }).format(date),
        MMM: new Intl.DateTimeFormat("en-US", { month: "short" }).format(date),
        MM: String(date.getMonth() + 1).padStart(2, "0"),
        M: String(date.getMonth() + 1),
        DDDD: new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(date),
        DDD: new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(date),
        DD: String(date.getDate()).padStart(2, "0"),
        D: String(date.getDate()),
        hh: String(hours12).padStart(2, "0"),
        h: String(hours12),
        hh24: String(hours24).padStart(2, "0"),
        h24: String(hours24),
        mm: String(date.getMinutes()).padStart(2, "0"),
        m: String(date.getMinutes()),
        ss: String(date.getSeconds()).padStart(2, "0"),
        s: String(date.getSeconds()),
        SSS: String(date.getMilliseconds()).padStart(3, "0"),
        S: String(date.getMilliseconds()).slice(0, 1),
        a: amOrPm,
        z: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    // Update the regular expression pattern to match placeholders as exact words
    const pattern = new RegExp("\\b(?:" + Object.keys(formatOptions).join("|") + ")\\b", "g");
    const formattedDate = format.replace(pattern, (match) => formatOptions[match]);
    return formattedDate;
};
function milliseconds(days = 0, hours = 0, minutes = 0, seconds = 0) {
    const totalSeconds = (days * 24 + hours) * 3600 + minutes * 60 + seconds;
    const milliseconds = totalSeconds * 1000;
    return milliseconds;
}
function getRelativeTimeString(date, units) {
    const timeUnitsInMs = Object.assign({ year: milliseconds(365), day: milliseconds(1), hour: milliseconds(0, 1), minute: milliseconds(0, 0, 1), second: milliseconds(0, 0, 0, 60) }, units);
    const rtf = new Intl.RelativeTimeFormat("en", { style: "narrow" });
    const timeMs = typeof date === "number" ? date : date.getTime();
    if (isNaN(timeMs)) {
        return "";
    }
    const delta = timeMs - Date.now();
    for (const [unit, amount] of Object.entries(timeUnitsInMs)) {
        if (Math.abs(delta) > amount) {
            return rtf.format(Math.round(delta / amount), unit);
        }
    }
    return "Just Now";
}
const getFilterTimeString = (start, end) => {
    if (start || end) {
        const f = {};
        if (start) {
            f["min"] = toUnixTimeStamp(start);
        }
        if (end) {
            f["max"] = toUnixTimeStamp(end);
        }
        return JSON.stringify(f);
    }
    return undefined;
};
const getFilterMinMaxString = (start, end) => {
    if (start || end) {
        const f = {};
        if (start) {
            f["min"] = toUnixTimeStamp(getStartingTimeOfDate(start));
        }
        if (end) {
            f["max"] = toUnixTimeStamp(getEndingTimeOfDate(end));
        }
        return JSON.stringify(f);
    }
    return undefined;
};
const offsetDate = (offsetDays) => addDaysOnDate(new Date(), offsetDays);
const offsetMonth = (offsetMonths) => {
    const dt = new Date();
    dt.setMonth(dt.getMonth() + offsetMonths);
    return dt;
};
const CommonDateTime = {
    yesterday: {
        min: () => getStartingTimeOfDate(addDaysOnDate(new Date(), -1)),
        max: () => getEndingTimeOfDate(addDaysOnDate(new Date(), -1)),
    },
    today: {
        min: () => getStartingTimeOfDate(new Date()),
        max: () => getEndingTimeOfDate(new Date()),
    },
    tomorrow: {
        min: () => getStartingTimeOfDate(addDaysOnDate(new Date(), 1)),
        max: () => getEndingTimeOfDate(addDaysOnDate(new Date(), 1)),
    },
    nthDate: {
        min: (n) => getStartingTimeOfDate(offsetDate(-n)),
        max: (n) => getEndingTimeOfDate(offsetDate(-n)),
    },
    nthMonth: {
        min: (n) => getStartingTimeOfDate(offsetMonth(-n)),
        max: (n) => getEndingTimeOfDate(offsetMonth(-n)),
    },
};
const CommonDateRange = {
    last7Days: {
        min: () => getStartingTimeOfDate(addDaysOnDate(new Date(), -7)),
        max: () => getEndingTimeOfDate(new Date()),
    },
    lastMonth: {
        min: () => getStartingTimeOfDate(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)),
        max: () => getEndingTimeOfDate(new Date(new Date().getFullYear(), new Date().getMonth(), 0)),
    },
    currentMonth: {
        min: () => getStartingTimeOfDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        max: () => getEndingTimeOfDate(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)),
    },
};
function toValidDate(input, fallback = undefined) {
    if (!input)
        return fallback;
    const date = new Date(input);
    return isNaN(date.getTime()) ? fallback : date;
}
function calculateDateDifferenceUnits(date1, date2, units = ["days", "hours", "mins", "secs"]) {
    let diffInMs = Math.abs(date1.getTime() - date2.getTime());
    const unitValues = {
        days: 0,
        hours: 0,
        mins: 0,
        secs: 0,
        milliseconds: 0,
    };
    if (units.includes("days")) {
        unitValues.days = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
        diffInMs -= unitValues.days * (1000 * 60 * 60 * 24);
    }
    if (units.includes("hours")) {
        unitValues.hours = Math.floor(diffInMs / (1000 * 60 * 60));
        diffInMs -= unitValues.hours * (1000 * 60 * 60);
    }
    if (units.includes("mins")) {
        unitValues.mins = Math.floor(diffInMs / (1000 * 60));
        diffInMs -= unitValues.mins * (1000 * 60);
    }
    if (units.includes("secs")) {
        unitValues.secs = Math.floor(diffInMs / 1000);
        diffInMs -= unitValues.secs * 1000;
    }
    if (units.includes("milliseconds")) {
        unitValues.milliseconds = diffInMs;
    }
    return unitValues;
}
function formatDateDifferenceSelectedUnits(date1, date2, units = ["days", "hours", "mins", "secs"]) {
    const unitValues = calculateDateDifferenceUnits(date1, date2, units);
    return mergeTimeUnits(unitValues, units);
}
const mergeTimeUnits = (record, units = ["days", "hours", "mins", "secs"]) => {
    const parts = [];
    for (const unit of units) {
        const value = record[unit];
        if (value > 0) {
            parts.push(`${value} ${unit.slice(0, -1)}${value > 1 ? "s" : ""}`);
        }
    }
    return parts.length ? parts.join(" ") : "";
};

export { CommonDateRange, CommonDateTime, addDaysOnDate, calculateDateDifferenceUnits, formatDate, formatDateDifferenceSelectedUnits, getEndingTimeOfDate, getFilterMinMaxString, getFilterTimeString, getRelativeTimeString, getStartingTimeOfDate, isSameDate, mergeTimeUnits, milliseconds, toUnixTimeStamp, toValidDate, toValidUnixTimeStamp };
//# sourceMappingURL=time.js.map
